import React, { useMemo, useState } from "react";
import classNames from "classnames";
import { Form } from "react-bootstrap";
import { Control, Controller, Noop, RefCallBack } from "react-hook-form";
import NumberFormat, { SyntheticInputEvent } from "react-number-format";
import { useCustomValidation } from "../hooks";
import { CustomValidationConfig } from "../services/types";
import { hasErrors, isConstraintARequiredConstraint } from "../services/utils";
import { FormErrorMessage } from "./FormErrorMessage";

interface NumberProps {
	label?: string;
	errors: any;
	name: string;
	suffix?: string;
	onChange?: (newVal?: number) => void;
	onBlur?: (newVal?: number) => void;
	allowNegative?: boolean;
	decimalScale?: number;
	format?: string;
	disabled?: boolean;
	control?: Control<any>;
	defaultValue?: any;
	placeholder?: string;
	valuePlaceholder?: string;
	help?: string;
	currency: string;
	withoutCurrency?: boolean;
	tabIndex?: string;
	allowEmpty?: boolean;
	customValidationConfig?: CustomValidationConfig;
	maxPermittedAmountOfDigits?: number;
}

export const FormMoneyInput = (props: NumberProps): JSX.Element => {
	const {
		onChange: onChangeInput,
		onBlur: onBlurInput,
		label,
		suffix,
		errors,
		name,
		control,
		allowNegative,
		format,
		decimalScale,
		defaultValue,
		disabled,
		placeholder,
		valuePlaceholder,
		help,
		currency,
		withoutCurrency,
		tabIndex,
		allowEmpty,
		customValidationConfig,
		maxPermittedAmountOfDigits,
	} = props;
	const [currentValue, setCurrentValue] = useState<string | number | undefined>(defaultValue);

	const curr = currency ? currency : withoutCurrency ? "" : "PLN";
	const renderInput = (onChange: any, renderProps: any) => {
		setCurrentValue(renderProps.value);

		return (
			<NumberFormat
				{...renderProps}
				placeholder={placeholder}
				tabIndex={tabIndex}
				disabled={disabled}
				format={format ? format : undefined}
				allowNegative={allowNegative ? allowNegative : false}
				decimalScale={decimalScale === undefined ? 2 : decimalScale}
				getInputRef={(el: any) => (el && renderProps.value === null ? (el.value = null) : null)}
				allowedDecimalSeparators={[".", ","]}
				onBlur={(e: SyntheticInputEvent) => {
					if (onBlurInput) {
						if (allowEmpty) onBlurInput(e.target.value !== "" ? Number(e.target.value) : undefined);
						else onBlurInput(Number(e.target.value) || 0);
					}
				}}
				className={classNames("form-control", { "is-invalid": hasErrors(errors, name) })}
				suffix={suffix === "" ? undefined : suffix ? ` ${suffix}` : ` ${curr}`}
				onValueChange={(values, sourceInfo) => {
					const newVal = values.value === "." ? "0." : values.floatValue;
					if (sourceInfo.event !== null && sourceInfo.source !== "prop") {
						onChange(newVal !== undefined ? newVal : null);
						if (onChangeInput && newVal) {
							const val = typeof newVal === "number" ? newVal : parseFloat(newVal);
							onChangeInput(val);
						} else if (onChangeInput) {
							onChangeInput(undefined);
						}
					} else if (onChangeInput) {
						onChange(newVal);
					}

					setCurrentValue(newVal);
				}}
				isAllowed={(values) => {
					const { floatValue } = values;
					if (maxPermittedAmountOfDigits && floatValue !== undefined) {
						const floatValueAsString = floatValue.toString();
						let numberIntegerPartAsString = floatValueAsString;

						if (floatValueAsString.includes("."))
							numberIntegerPartAsString = numberIntegerPartAsString.split(".")[0];

						return numberIntegerPartAsString.length <= maxPermittedAmountOfDigits;
					}
					return true;
				}}
			/>
		);
	};

	const isFieldRequired = useMemo(
		() => customValidationConfig?.types.some(({ constraint }) => isConstraintARequiredConstraint(constraint)),
		[customValidationConfig?.types]
	);

	useCustomValidation({ name, value: currentValue, customValidationConfig });

	return (
		<Form.Group className="form-group" controlId={name}>
			{label && <Form.Label>{isFieldRequired ? `${label} *` : label}</Form.Label>}
			<Controller
				control={control}
				name={name}
				defaultValue={defaultValue}
				render={({ field: { onChange, ...renderProps } }) =>
					valuePlaceholder ? (
						<div className={"d-flex flex-grow-1 align-items-center"}>
							{renderInput(onChange, renderProps)}
							{renderProps.value ? (
								<small className={"input-value-placeholder"}>{valuePlaceholder}</small>
							) : null}
						</div>
					) : (
						renderInput(onChange, renderProps)
					)
				}
			/>
			{help && <Form.Text muted>{help}</Form.Text>}
			<FormErrorMessage errors={errors} name={name} />
		</Form.Group>
	);
};

export default FormMoneyInput;
